import { useEffect, useState } from "react"
import { getSubscriptionPlans } from "../../services/subscription-service"
import { createSession } from "../../services/employer-service"
import { useToast } from "../../context/toast"
import { useEntitlements } from "../../context/entitlements"

interface Plan {
  name: string
  label: string
  tier: string
  billingInterval: string
  price: number
  stripePriceId: string
  sortOrder: number
  includedJobPosts: number | null
  includedJobPromotions: number | null
  includedInvitations: number | null
  includedNotifyCampaigns: number | null
  includedEventCreates: number | null
  monthlyCreditStipend: number | null
}

const TIER_ORDER = ["SOLO", "BUSINESS", "UNLIMITED"]
const RECOMMENDED_TIER = "BUSINESS"

// -1 = nelimitat. Construiește lista de beneficii afișată pe cardul fiecărui plan.
const quotaLine = (n: number | null | undefined, unlimited: string, some: (v: number) => string): string | null => {
  if (n === null || n === undefined || n === 0) return null
  if (n === -1) return unlimited
  return some(n)
}

const planFeatures = (p: Plan): string[] =>
  [
    quotaLine(p.includedJobPosts, "Joburi nelimitate", (v) => `${v} joburi / lună`),
    quotaLine(p.includedJobPromotions, "Promovări nelimitate", (v) => `${v} promovări job / lună`),
    quotaLine(p.includedInvitations, "Invitații nelimitate", (v) => `${v} invitații / lună`),
    quotaLine(p.includedNotifyCampaigns, "Notificări nelimitate", (v) => `${v} campanii notificare / lună`),
    quotaLine(p.includedEventCreates, "Evenimente nelimitate", (v) => `${v} eveniment / lună`),
    quotaLine(p.monthlyCreditStipend, "", (v) => `Pachet lunar de ${v} credite`),
  ].filter((x): x is string => !!x)

const formatPrice = (lei: number) => Math.round(lei) + " RON"

export const SubscriptionPlans = () => {
  const [plans, setPlans] = useState<Plan[]>([])
  const [interval, setInterval] = useState<"MONTHLY" | "ANNUAL">("MONTHLY")
  const [loading, setLoading] = useState(true)
  const [redirecting, setRedirecting] = useState<string | null>(null)
  const { notify } = useToast()
  // Cotele/planul activ vin din sursa partajată (o singură cerere pe pagină). Polling-ul de după
  // abonare e declanșat din CreditsPage — pagina pe care aterizează redirect-ul Stripe.
  const { usage } = useEntitlements()

  useEffect(() => {
    let cancelled = false

    // Catalogul de planuri e independent de usage: o eroare pe entitlements nu are voie să ascundă
    // catalogul (usage-ul e doar decor peste el).
    getSubscriptionPlans()
      .then((pl) => {
        if (!cancelled) setPlans(Array.isArray(pl) ? pl : [])
      })
      .catch(() => {
        if (!cancelled) setPlans([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const planFor = (tier: string): Plan | undefined => {
    const exact = plans.find((p) => p.tier === tier && p.billingInterval === interval)
    if (exact) return exact
    // fallback: dacă nu există varianta pe intervalul ales, arată varianta lunară
    return plans.find((p) => p.tier === tier && p.billingInterval === "MONTHLY")
  }

  // Procentul de economie anual vs lunar × 12 (pentru eticheta de pe toggle).
  const annualSavings = (() => {
    const m = plans.find((p) => p.tier === "BUSINESS" && p.billingInterval === "MONTHLY")
    const a = plans.find((p) => p.tier === "BUSINESS" && p.billingInterval === "ANNUAL")
    if (!m || !a || m.price <= 0) return 0
    return Math.round((1 - a.price / (m.price * 12)) * 100)
  })()

  const hasAnnual = plans.some((p) => p.billingInterval === "ANNUAL")

  const onChoose = async (plan: Plan) => {
    setRedirecting(plan.tier)
    try {
      // Cont cu abonament activ → backend întoarce billing portal (priceId ignorat). Altfel checkout.
      const res = await createSession(usage?.hasActivePlan ? undefined : plan.stripePriceId)
      if (res?.url) {
        window.location.href = res.url
      } else {
        notify("Nu am putut deschide plata. Încearcă din nou.", "error")
      }
    } catch {
      notify("Nu am putut deschide plata. Încearcă din nou.", "error")
    } finally {
      setRedirecting(null)
    }
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
        <div className="h-64 rounded-[20px] bg-gray-100 animate-pulse" />
        <div className="h-64 rounded-[20px] bg-gray-100 animate-pulse" />
        <div className="h-64 rounded-[20px] bg-gray-100 animate-pulse" />
      </div>
    )
  }

  // Fără planuri active (ex. Stripe neconfigurat încă) → nu afișăm secțiunea deloc.
  if (plans.length === 0) return null

  // Abonatul își vede planul curent + cotele în cardul „Abonamentul meu" (MySubscription) de deasupra;
  // aici rămâne doar catalogul, cu titlu de upsell pentru neabonați și de „schimbă planul" pentru abonați.
  const hasPlan = !!usage?.hasActivePlan

  return (
    <div
      className="mb-5 mt-4 rounded-[24px] p-4 sm:p-5"
      style={{ background: "linear-gradient(135deg,#eef2ff 0%,#f6f8ff 55%,#eefcf3 100%)", border: "1px solid #dbe3ff" }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
        <div>
          <span className="inline-block mb-1.5 rounded-full bg-figmaPrimary/10 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-figmaPrimary">
            {hasPlan ? "Schimbă sau upgradează" : "Cel mai bun raport preț / valoare"}
          </span>
          <h3 className="text-[20px] sm:text-[24px] font-black leading-none text-figmaBlack">
            {hasPlan ? "Planurile disponibile" : "Abonează-te și economisește"}
          </h3>
          <p className="text-[12.5px] text-figmaDarkGray mt-1.5">Cote incluse lunar + credite bonus. Recrutarea de rutină, acoperită — la un preț fix.</p>
        </div>
        {hasAnnual && (
          <div className="inline-flex items-center rounded-full bg-gray-100 p-0.5 self-start">
            <button
              onClick={() => setInterval("MONTHLY")}
              className={`px-3 py-1.5 rounded-full text-[12px] font-bold transition ${interval === "MONTHLY" ? "bg-white text-figmaBlack shadow-sm" : "text-figmaDarkGray"}`}
            >
              Lunar
            </button>
            <button
              onClick={() => setInterval("ANNUAL")}
              className={`px-3 py-1.5 rounded-full text-[12px] font-bold transition ${interval === "ANNUAL" ? "bg-white text-figmaBlack shadow-sm" : "text-figmaDarkGray"}`}
            >
              Anual{annualSavings > 0 ? ` −${annualSavings}%` : ""}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
        {TIER_ORDER.map((tier) => {
          const plan = planFor(tier)
          if (!plan) return null
          const isRecommended = tier === RECOMMENDED_TIER
          // „Planul tău" doar pe varianta exactă (treaptă + interval) pe care e abonat acum,
          // ca să poată trece de pe lunar pe anual (și invers) fără ca butonul să fie blocat.
          const isCurrent = !!usage?.hasActivePlan && usage.tier === tier && usage.billingInterval === plan.billingInterval
          const purchasable = !!plan.stripePriceId
          const hasUnlimited = [
            plan.includedJobPosts,
            plan.includedJobPromotions,
            plan.includedInvitations,
            plan.includedNotifyCampaigns,
            plan.includedEventCreates,
          ].some((v) => v === -1)
          const features = planFeatures(plan)
          const perMonth = plan.billingInterval === "ANNUAL" ? plan.price / 12 : plan.price
          return (
            <div
              key={tier}
              className="bg-white rounded-[20px] px-4 pt-4 pb-4 flex flex-col"
              style={{
                border: isCurrent ? "2px solid #1e3a8a" : isRecommended ? "2px solid #486af6" : "1px solid #e5e7eb",
                boxShadow: isRecommended ? "0 10px 28px rgba(72,106,246,0.20)" : "0 1px 2px rgba(16,24,40,0.04)",
              }}
            >
              <div className="flex items-center justify-between mb-1.5" style={{ minHeight: 18 }}>
                <p className="text-[11px] font-bold uppercase tracking-wide text-figmaDarkGray">{plan.label}</p>
                {isCurrent ? (
                  <span className="text-white text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#1e3a8a" }}>PLANUL TĂU</span>
                ) : isRecommended ? (
                  <span className="bg-figmaContrast text-white text-[9px] font-bold px-1.5 py-0.5 rounded">CEL MAI POPULAR</span>
                ) : null}
              </div>

              <div className="flex items-baseline gap-1 mt-1">
                <span className={`text-[30px] font-black leading-none ${isRecommended ? "text-figmaPrimary" : "text-figmaBlack"}`}>
                  {formatPrice(perMonth)}
                </span>
                <span className="text-[12px] text-figmaDarkGray">/ lună</span>
              </div>
              {plan.billingInterval === "ANNUAL" && (
                <p className="text-[10px] text-figmaDarkGray mt-0.5">{formatPrice(plan.price)} facturat anual</p>
              )}

              <div className="flex-1 mt-3 space-y-1.5">
                {features.map((f) => (
                  <div key={f} className="flex items-start gap-1.5">
                    <span className="text-figmaContrast text-[12px] leading-tight mt-px">✓</span>
                    <span className="text-[12px] text-figmaBlack leading-tight">{f}</span>
                  </div>
                ))}
              </div>

              {hasUnlimited && (
                <p className="text-[10px] text-figmaDarkGray mt-2">* Se aplică o politică de uz corect.</p>
              )}

              <button
                onClick={() => purchasable && onChoose(plan)}
                disabled={isCurrent || !purchasable || redirecting === tier}
                style={isCurrent ? { background: "#e8ecfb", color: "#1e3a8a" } : undefined}
                className={`mt-3 w-full py-2.5 rounded-[10px] text-[12px] font-bold transition-opacity disabled:cursor-default ${
                  isCurrent ? "" : !purchasable ? "bg-gray-100 text-figmaDarkGray" : isRecommended ? "bg-figmaPrimary text-white" : "bg-gray-100 text-gray-700"
                }`}
              >
                {isCurrent
                  ? "Planul tău"
                  : !purchasable
                  ? "Disponibil în curând"
                  : redirecting === tier
                  ? "..."
                  : usage?.hasActivePlan
                  ? "Schimbă planul"
                  : "Alege"}
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
